<template>
  <main class="promo-main">
    <header class="promo-toolbar">
      <div class="toolbar-actions">
        <button class="ghost-btn" @click="workbench.clearHistory">
          清空历史
        </button>

        <button class="ghost-btn" @click="toggleSelectAll">
          {{ isAllSelected ? "取消全选" : "选择全部" }}
        </button>

        <button
          class="download-btn"
          :disabled="!workbench.videoGen.selectedIdxs.length"
          @click="downloadSelected"
        >
          <img class="download-icon" :src="downloadIcon" alt="" />
          <span>下载已选 ({{ workbench.videoGen.selectedIdxs.length }})</span>
        </button>

        <button
          class="primary-btn compact"
          :disabled="!workbench.videoGen.selectedIdxs.length"
          @click="goToVideoWithSelection"
        >
          将所选图片用于视频生成
        </button>
        <button
          class="ghost-btn toolbar-secondary"
          :disabled="!workbench.videoGen.selectedIdxs.length"
          @click="goToFissionWithSelection"
        >
          将所选图片用于裂变
        </button>

        <button
          class="ghost-btn toolbar-secondary"
          :disabled="!workbench.videoGen.selectedIdxs.length"
          @click="goToSizeChartWithSelection"
        >
          将所选图片用于尺码底图
        </button>
      </div>
    </header>

    <section class="promo-gallery">
      <div class="gallery-grid">
        <PromoGalleryCard
          v-for="(item, index) in workbench.promo.items"
          :key="index"
          :index="index"
        />
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed } from "vue"; // 必须引入 computed
import { useRouter } from "vue-router";
import PromoGalleryCard from "./PromoGalleryCard.vue";
import { useWorkbenchStore } from "@/stores/useWorkbenchStore";
import { imageApi } from "@/api/imageApi";
import { normalizeImageRefsForServer } from "@/utils/normalizeImageRefForServer";

const router = useRouter();
const workbench = useWorkbenchStore();
const downloadIcon = "/download.svg";

// ==============================
// 新增：全选与取消全选逻辑
// ==============================
const isAllSelected = computed(() => {
  const totalItems = workbench.promo.items.length;
  return (
    totalItems > 0 && workbench.videoGen.selectedIdxs.length === totalItems
  );
});

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    workbench.videoGen.selectedIdxs = []; // 取消全选
  } else {
    // 全选 (将所有图片的索引推入数组)
    workbench.videoGen.selectedIdxs = workbench.promo.items.map(
      (_, index) => index
    );
  }
};

// ==============================
// 新增：区分单图和多图 ZIP 下载逻辑
// ==============================
const downloadSelected = async () => {
  const selected = workbench.videoGen.selectedIdxs;
  if (selected.length === 0) return;

  // 提取出选中图片对应的 URL 或 Base64 数据
  const getUrl = (item) =>
    item.url || item.imageUrl || item.image || item.imageBase64;
  const urls = selected
    .map((idx) => getUrl(workbench.promo.items[idx]))
    .filter(Boolean);

  if (urls.length === 0) return alert("图片链接无效");

  if (urls.length === 1) {
    // 【情况 A】只选择了一张图，直接触发单图下载
    const link = document.createElement("a");
    link.href = urls[0];
    link.download = `image_${selected[0] + 1}.png`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    // 【情况 B】选择多张图，请求后端打成 ZIP 压缩包 (避免跨域问题)
    try {
      // 注意：这里的 /api/download-zip 需确保与 Vite/Vue 的代理配置一致
      const normalizedUrls = await normalizeImageRefsForServer(urls);
      const blob = await imageApi.downloadZip({ urls: normalizedUrls });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "selected_images.zip"; // 下载下来的压缩包名称
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href); // 释放内存
    } catch (error) {
      console.error("打包下载失败:", error);
      alert(error?.message || "下载压缩包失败，请检查网络或后端服务");
    }
  }
};

// ==============================
// 原有业务逻辑保持不变
// ==============================
const goToVideoWithSelection = () => {
  if (!workbench.videoGen.selectedIdxs.length) return;
  workbench.videoGen.selectedIdx =
    workbench.videoGen.selectedIdxs[workbench.videoGen.selectedIdxs.length - 1];
  router.push("/promo-video");
};

const goToFissionWithSelection = () => {
  const ok = workbench.prepareFissionFromSelection();
  if (!ok) return;
  router.push("/fission");
};

const goToSizeChartWithSelection = () => {
  const ok = workbench.prepareSizeChartBgFromSelection();
  if (!ok) return;
  router.push("/size-chart");
};
</script>
